/**
 * Account-level chat preferences — chat colour, the default chat background and
 * the notification mutes.
 *
 *   const { preferences, setChatColor, setChatBackground } = useChatPreferences()
 *
 * Server-backed, so a change on one device shows up on the others. Contrast the
 * auto-download toggles, which stay in `localStorage` on purpose: those are a
 * per-device bandwidth policy, these are account settings.
 *
 * NOTE `preferences.theme` is stored and returned, but this app does NOT apply
 * it — light/dark stays device-local in `ThemeContext`, as it was before. The
 * field is kept so a host app that wants a synced theme can use it.
 *
 * Shared across mounts (settings tab, theme provider, conversation panel) via a
 * module-level cache, so N consumers cost one request.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { getChatPreferences, setChatPreferences } from '@/sdk/instavcMessenger-instance'
import type { ChatPreferences, ThemeMode } from '@/sdk/instavcMessenger'

const DEFAULTS: ChatPreferences = {
  theme: 'system',
  chatBackground: null,
  chatColor: null,
  muteGroups: false,
  muteDirect: false,
}

let cached: ChatPreferences | null = null
let inflight: Promise<ChatPreferences> | null = null
/** Consumers to re-render when another mount changes a preference. */
const subscribers = new Set<(p: ChatPreferences) => void>()

function publish(next: ChatPreferences): void {
  cached = next
  for (const fn of subscribers) fn(next)
}

function load(userId: string): Promise<ChatPreferences> {
  if (cached) return Promise.resolve(cached)
  inflight ??= getChatPreferences(userId)
    .then((p) => { publish(p); return p })
    .finally(() => { inflight = null })
  return inflight
}

/** Drop the shared cache — call on logout so the next user starts clean. */
export function resetChatPreferencesCache(): void {
  cached = null
  inflight = null
}

interface UseChatPreferences {
  preferences: ChatPreferences
  setTheme: (theme: ThemeMode) => Promise<void>
  setChatBackground: (background: string | null) => Promise<void>
  setChatColor: (color: string | null) => Promise<void>
  setMuteGroups: (mute: boolean) => Promise<void>
  setMuteDirect: (mute: boolean) => Promise<void>
  isLoading: boolean
  /** False until the server round trip for this account has completed at
   *  least once (success or failure). `isLoading` is not a substitute: it
   *  starts false on every fresh mount, so a consumer that only checks
   *  `!isLoading` sees "not loading" and "loaded" as the same thing during
   *  the gap before the first response, when `preferences` is still
   *  `DEFAULTS`. Anything that would misbehave on a default it mistook for
   *  the real value (an alert that should have been muted, say) should wait
   *  on this instead. */
  preferencesLoaded: boolean
  error: string | null
}

export function useChatPreferences(): UseChatPreferences {
  const me = useAuthStore((s) => s.user)
  const presenceId = useAuthStore((s) => s.presenceId)
  const [preferences, setPreferences] = useState<ChatPreferences>(cached ?? DEFAULTS)
  const [isLoading, setIsLoading] = useState(false)
  const [preferencesLoaded, setPreferencesLoaded] = useState<boolean>(() => cached !== null)
  const [error, setError] = useState<string | null>(null)

  // Every mount tracks the shared value, so changing the theme in Settings
  // repaints the app without prop-drilling or a reload.
  useEffect(() => {
    subscribers.add(setPreferences)
    return () => { subscribers.delete(setPreferences) }
  }, [])

  useEffect(() => {
    if (!me?.id || !presenceId) return
    let cancelled = false
    setIsLoading(true)
    load(me.id)
      .then((p) => { if (!cancelled) setPreferences(p) })
      .catch(() => { if (!cancelled) setError('failed to load preferences') })
      .finally(() => { if (!cancelled) { setIsLoading(false); setPreferencesLoaded(true) } })
    return () => { cancelled = true }
  }, [me?.id, presenceId])

  const patch = useCallback(
    async (change: Partial<ChatPreferences>) => {
      if (!me?.id) return
      const previous = cached ?? DEFAULTS
      publish({ ...previous, ...change })   // optimistic — a theme flip must feel instant
      setError(null)
      try {
        publish(await setChatPreferences(me.id, change))
      } catch {
        publish(previous)
        setError('failed to save')
      }
    },
    [me?.id],
  )

  return {
    preferences,
    setTheme: useCallback((theme: ThemeMode) => patch({ theme }), [patch]),
    setChatBackground: useCallback(
      (chatBackground: string | null) => patch({ chatBackground }),
      [patch],
    ),
    setChatColor: useCallback((chatColor: string | null) => patch({ chatColor }), [patch]),
    setMuteGroups: useCallback((muteGroups: boolean) => patch({ muteGroups }), [patch]),
    setMuteDirect: useCallback((muteDirect: boolean) => patch({ muteDirect }), [patch]),
    isLoading,
    preferencesLoaded,
    error,
  }
}
